import { ComponentType } from 'react';
import {
  Home, List, Target, RefreshCw, BarChart2, TrendingUp,
  Wallet, Box, Calculator, FileText,
  Users, ShoppingCart, Receipt, PackageOpen, Split, ListChecks, Users2,
} from 'lucide-react';

export interface SeccionModulo {
  to: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  /** Si es false, no aparece en la barra inferior de móvil (espacio limitado). */
  enBottomNav?: boolean;
}

export interface Modulo {
  id: string;
  nombre: string;
  /** Frase corta para la tarjeta del hub. */
  descripcion: string;
  Icon: ComponentType<{ className?: string }>;
  /** Prefijo de ruta del módulo, p. ej. '/finanzas'. */
  base: string;
  /** Ruta a la que entra el módulo desde el hub. */
  inicio: string;
  /** Clases Tailwind del acento del módulo. */
  acento: {
    texto: string;
    fondoSuave: string;
    boton: string;
    borde: string;
  };
  /**
   * false = todavía no está construido; sale en el hub como «Pronto».
   * Ojo: esto es si el módulo EXISTE, no si un usuario concreto puede usarlo.
   * El permiso por usuario vive en `usuarios/{uid}.modulos[id]`.
   */
  disponible: boolean;
  secciones: SeccionModulo[];
}

export const MODULOS: Modulo[] = [
  {
    id: 'finanzas',
    nombre: 'Finanzas',
    descripcion: 'Cuentas, transacciones, presupuestos e inversiones.',
    Icon: Wallet,
    base: '/finanzas',
    inicio: '/finanzas/dashboard',
    acento: {
      texto: 'text-blue-600 dark:text-blue-400',
      fondoSuave: 'bg-blue-50 dark:bg-blue-900/30',
      boton: 'bg-blue-600 hover:bg-blue-700',
      borde: 'border-blue-200 dark:border-blue-800',
    },
    disponible: true,
    secciones: [
      { to: '/finanzas/dashboard',    label: 'Dashboard',     Icon: Home,      enBottomNav: true },
      { to: '/finanzas/transacciones', label: 'Transacciones', Icon: List,      enBottomNav: true },
      { to: '/finanzas/presupuestos',  label: 'Presupuestos',  Icon: Target,    enBottomNav: true },
      { to: '/finanzas/periodicos',    label: 'Periódicos',    Icon: RefreshCw, enBottomNav: true },
      { to: '/finanzas/informes',      label: 'Informes',      Icon: BarChart2, enBottomNav: true },
      { to: '/finanzas/inversiones',   label: 'Inversiones',   Icon: TrendingUp, enBottomNav: true },
    ],
  },
  {
    id: 'taller3d',
    nombre: 'Taller 3D',
    descripcion: 'Calcula el coste real de una impresión y su precio de venta.',
    Icon: Box,
    base: '/taller3d',
    inicio: '/taller3d/calculadora',
    acento: {
      texto: 'text-orange-600 dark:text-orange-400',
      fondoSuave: 'bg-orange-50 dark:bg-orange-900/30',
      boton: 'bg-orange-600 hover:bg-orange-700',
      borde: 'border-orange-200 dark:border-orange-800',
    },
    disponible: true,
    secciones: [
      { to: '/taller3d/calculadora',   label: 'Calculadora',  Icon: Calculator, enBottomNav: true },
      { to: '/taller3d/presupuestos',  label: 'Presupuestos', Icon: FileText,   enBottomNav: true },
    ],
  },
  {
    id: 'clientes',
    nombre: 'Clientes',
    descripcion: 'Fichas de clientes, sus datos fiscales y su historial.',
    Icon: Users,
    base: '/clientes',
    inicio: '/clientes',
    acento: {
      texto: 'text-purple-600 dark:text-purple-400',
      fondoSuave: 'bg-purple-50 dark:bg-purple-900/30',
      boton: 'bg-purple-600 hover:bg-purple-700',
      borde: 'border-purple-200 dark:border-purple-800',
    },
    disponible: true,
    secciones: [
      { to: '/clientes', label: 'Clientes', Icon: Users, enBottomNav: true },
    ],
  },
  {
    id: 'prestamos',
    nombre: 'Préstamos',
    descripcion: 'Qué has dejado, a quién y si ya ha vuelto.',
    Icon: PackageOpen,
    base: '/prestamos',
    inicio: '/prestamos',
    acento: {
      texto: 'text-cyan-600 dark:text-cyan-400',
      fondoSuave: 'bg-cyan-50 dark:bg-cyan-900/30',
      boton: 'bg-cyan-600 hover:bg-cyan-700',
      borde: 'border-cyan-200 dark:border-cyan-800',
    },
    disponible: true,
    secciones: [
      { to: '/prestamos', label: 'Préstamos', Icon: PackageOpen, enBottomNav: true },
    ],
  },
  {
    id: 'amedias',
    nombre: 'A medias',
    descripcion: 'Gastos compartidos de viajes y comidas: quién debe a quién.',
    Icon: Split,
    base: '/amedias',
    inicio: '/amedias',
    acento: {
      texto: 'text-emerald-600 dark:text-emerald-400',
      fondoSuave: 'bg-emerald-50 dark:bg-emerald-900/30',
      boton: 'bg-emerald-600 hover:bg-emerald-700',
      borde: 'border-emerald-200 dark:border-emerald-800',
    },
    disponible: true,
    secciones: [
      { to: '/amedias', label: 'Planes', Icon: Split, enBottomNav: true },
    ],
  },
  {
    id: 'tareas',
    nombre: 'Tareas',
    descripcion: 'Las tareas de casa, repartidas y a la vista de todos.',
    Icon: ListChecks,
    base: '/tareas',
    inicio: '/tareas',
    acento: {
      texto: 'text-lumbre-600 dark:text-lumbre-400',
      fondoSuave: 'bg-lumbre-50 dark:bg-lumbre-900/30',
      boton: 'bg-lumbre-600 hover:bg-lumbre-700',
      borde: 'border-lumbre-200 dark:border-lumbre-800',
    },
    disponible: true,
    secciones: [
      { to: '/tareas',       label: 'Semana', Icon: ListChecks, enBottomNav: true },
      { to: '/tareas/compra', label: 'Compra', Icon: ShoppingCart, enBottomNav: true },
      { to: '/tareas/casas', label: 'Casas',  Icon: Users2,     enBottomNav: true },
    ],
  },
  // ── Roadmap: visibles en el hub, todavía sin implementar ──────────────────
  {
    id: 'pedidos',
    nombre: 'Pedidos',
    descripcion: 'Del presupuesto aceptado a la entrega. Próximamente.',
    Icon: ShoppingCart,
    base: '/pedidos',
    inicio: '/pedidos',
    acento: {
      texto: 'text-teal-600 dark:text-teal-400',
      fondoSuave: 'bg-teal-50 dark:bg-teal-900/30',
      boton: 'bg-teal-600 hover:bg-teal-700',
      borde: 'border-teal-200 dark:border-teal-800',
    },
    disponible: false,
    secciones: [],
  },
  {
    id: 'facturas',
    nombre: 'Facturación',
    descripcion: 'Facturas, series e IVA. Próximamente.',
    Icon: Receipt,
    base: '/facturas',
    inicio: '/facturas',
    acento: {
      texto: 'text-rose-600 dark:text-rose-400',
      fondoSuave: 'bg-rose-50 dark:bg-rose-900/30',
      boton: 'bg-rose-600 hover:bg-rose-700',
      borde: 'border-rose-200 dark:border-rose-800',
    },
    disponible: false,
    secciones: [],
  },
];

/**
 * Módulos con los que nace una cuenta nueva, sin que intervenga nadie. El resto
 * los activa un administrador. Debe cuadrar con creaPerfilLimpio() en
 * firestore.rules, que es quien de verdad lo permite.
 * Con las opciones de pago, aquí entraría la versión gratuita (docs/premium.md).
 */
export const MODULOS_POR_DEFECTO = ['finanzas', 'amedias', 'tareas'];

/** Módulos ya construidos, con independencia de quién los pueda usar. */
export const MODULOS_ACTIVOS = MODULOS.filter(m => m.disponible);

/** Módulo al que pertenece una ruta; undefined en el hub, config o login. */
export function moduloDeRuta(pathname: string): Modulo | undefined {
  return MODULOS.find(m => pathname === m.base || pathname.startsWith(m.base + '/'));
}

/**
 * Los que este usuario puede usar: construidos y además activados para él.
 * `tieneModulo` sale de usePerfil(); se pasa como argumento para que este
 * archivo siga siendo configuración pura, sin depender de React.
 */
export function modulosPermitidos(tieneModulo: (id: string) => boolean): Modulo[] {
  return MODULOS_ACTIVOS.filter(m => tieneModulo(m.id));
}

/** Colecciones de Firestore que pertenecen a cada módulo.
 *  Debe cuadrar con `coleccionesDeModulo()` en firestore.rules: si aquí
 *  añades una y allí no, el módulo se activará pero no podrá escribir. */
export const COLECCIONES_POR_MODULO: Record<string, string[]> = {
  finanzas: [
    'cuentas', 'categorias', 'transacciones', 'presupuestos', 'traspasos',
    'traspasosRecurrentes', 'transaccionesRecurrentes', 'inversiones',
    'aportacionesInversion', 'retirosInversion', 'valoracionesInversion',
    // Qué planes de A medias pasa cada uno a sus Finanzas (privado, por usuario)
    'sincronizacionesPlan',
  ],
  taller3d: ['perfilesFilamento', 'perfilesImpresora', 'presupuestos3d'],
  clientes: ['clientes'],
  prestamos: ['prestamos'],
  // 'planes' no está en moduloDeColeccion(): al ser multiusuario lleva su
  // propio bloque en las reglas, con los gastos y pagos como subcolecciones.
  amedias: ['planes'],
  // Igual que 'planes': compartida, con sus tareas e historial dentro.
  tareas: ['casas'],
};
